import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import TeacherClassPicker from "@/components/TeacherClassPicker";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  classKey,
  classLabelFromKey,
  getSessionUser,
} from "@/lib/eisStore";
import {
  decodeClassKey,
  getTimetableRecordByClass,
  isSupabaseConfigured,
  uploadTimetable,
} from "@/lib/supabaseOps";
import { useSupabaseFileUrl } from "@/hooks/useSupabaseFileUrl";

function mapTimetableRecord(record) {
  if (!record) return null;
  return {
    filePath: record.file_path,
    fileName: "Timetable image",
    bucket: "class-files",
  };
}

function StudentTimetable({ user }) {
  const studentClassKey = classKey(user.standard, user.division);
  const [timetableRecord, setTimetableRecord] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!isSupabaseConfigured) {
        if (active) setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const record = await getTimetableRecordByClass(user.standard, user.division);
        if (active) {
          setTimetableRecord(mapTimetableRecord(record));
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setTimetableRecord(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [user.standard, user.division]);

  const timetableUrl = useSupabaseFileUrl(timetableRecord, "class-files");

  return (
    <AppShell>
      <div data-testid="timetable-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Timetable
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Your class timetable appears here.
          </p>
        </div>

        <Card>
          <CardHeader>
          <CardTitle style={{ color: "#0b1f7a" }}>
              {classLabelFromKey(studentClassKey)}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <p className="text-sm text-slate-500">Loading timetable...</p>
            ) : timetableUrl ? (
              <img
                src={timetableUrl}
                alt="Class timetable"
                className="w-full rounded-xl border border-slate-200"
              />
            ) : (
              <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
                <p
                  data-testid="timetable-empty"
                  className="text-lg font-semibold text-slate-700"
                >
                  Class Teacher has not uploaded yet
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}

function TeacherTimetable({ user }) {
  const [teacherClassKey, setTeacherClassKey] = useState(user.assignedClassKey || "");
  const [timetableRecord, setTimetableRecord] = useState(null);
  const [loading, setLoading] = useState(Boolean(teacherClassKey));
  const [refreshTick, setRefreshTick] = useState(0);
  const timetableUrl = useSupabaseFileUrl(timetableRecord, "class-files");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!teacherClassKey || !isSupabaseConfigured) {
        if (active) {
          setTimetableRecord(null);
          setLoading(false);
        }
        return;
      }
      const { standard, division } = decodeClassKey(teacherClassKey);
      try {
        setLoading(true);
        const record = await getTimetableRecordByClass(standard, division);
        if (active) {
          setTimetableRecord(mapTimetableRecord(record));
        }
      } catch (error) {
        console.error(error);
        if (active) {
          setTimetableRecord(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [teacherClassKey, refreshTick]);

  const upload = async (file) => {
    if (!teacherClassKey) {
      toast.error("Please select a class first");
      return;
    }
    if (!file) {
      toast.error("Please choose an image");
      return;
    }
    if (!isSupabaseConfigured) {
      toast.error("Supabase is not configured");
      return;
    }

    setUploading(true);
    try {
      const { standard, division } = decodeClassKey(teacherClassKey);
      await uploadTimetable(file, standard, division);
      setRefreshTick((current) => current + 1);
      toast.success("Timetable uploaded");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppShell>
      <div data-testid="timetable-page" className="space-y-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold" style={{ color: "#0b1f7a" }}>
            Timetable
          </h2>
          <p className="text-slate-500 mt-1 text-sm">
            Upload the timetable image for your class.
          </p>
        </div>

        <TeacherClassPicker
          teacher={user}
          onChange={(value) => setTeacherClassKey(value)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-6">
          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>Upload Timetable Image</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Select image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => upload(e.target.files?.[0])}
                  className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:rounded-full file:border-0 file:bg-[#0b1f7a] file:px-4 file:py-2 file:text-white hover:file:opacity-90"
                />
              </div>

              {uploading && <p className="text-sm text-slate-500">Uploading...</p>}

              {timetableUrl && (
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-800">
                    Current upload
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{timetableRecord?.fileName || "Timetable image"}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle style={{ color: "#0b1f7a" }}>
                {teacherClassKey ? classLabelFromKey(teacherClassKey) : "Class Preview"}
              </CardTitle>
            </CardHeader>
              <CardContent className="p-6">
              {loading ? (
                <p className="text-sm text-slate-500">Loading timetable...</p>
              ) : timetableUrl ? (
                <img
                  src={timetableUrl}
                  alt="Uploaded timetable"
                  className="w-full rounded-xl border border-slate-200"
                />
              ) : (
                <div className="p-12 flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-slate-300">
                  <p
                    data-testid="timetable-empty"
                    className="text-lg font-semibold text-slate-700"
                  >
                    Class Teacher has not uploaded yet
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

export default function Timetable() {
  const user = getSessionUser();
  if (user?.role === "teacher") {
    return <TeacherTimetable user={user} />;
  }
  return <StudentTimetable user={user || {}} />;
}
